// ahri_v5 — domain/formatting.js

export function hasAuthHeaders(headers) {
  return !!(headers["X-API-Key"] || headers.Authorization);
}

/**
 * Decodes a JWT's payload claims without verifying its signature (verification
 * happens server-side; this just reads values the token already carries).
 * Returns null for anything that isn't a well-formed JWT.
 */
export function decodeJwtClaims(token) {
  try {
    const base64Url = String(token || "").split(".")[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const binary = atob(padded);
    const percentEncoded = Array.from(binary)
      .map((c) => `%${c.charCodeAt(0).toString(16).padStart(2, "0")}`)
      .join("");
    return JSON.parse(decodeURIComponent(percentEncoded));
  } catch {
    return null;
  }
}

export function decodeHtmlEntities(text) {
  if (!text || typeof text !== "string") return text;
  if (typeof document === "undefined") return text;
  const textarea = document.createElement("textarea");
  let decoded = text;
  let prev = "";
  let iterations = 0;
  while (decoded !== prev && iterations < 8) {
    prev = decoded;
    textarea.innerHTML = decoded;
    decoded = textarea.value;
    iterations += 1;
  }
  return decoded;
}

export function resolveAssetUrl(url, endpoint) {
  const value = String(url || "").trim();
  if (!value) return "";
  if (value.startsWith("http://") || value.startsWith("https://")) return value;
  if (value.startsWith("/")) return `${String(endpoint || "").replace(/\/+$/, "")}${value}`;
  return value;
}
export function formatScore(value) {
  if (!Number.isFinite(value)) return "-";
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}
export function normalizeInteractionModes(value) {
  if (!Array.isArray(value)) return ["filters", "qa"];
  const cleaned = value
    .map((mode) => String(mode || "").trim().toLowerCase())
    .filter((mode, idx, arr) => (mode === "filters" || mode === "qa") && arr.indexOf(mode) === idx);
  return cleaned.length ? cleaned : ["filters", "qa"];
}

export function normalizeInteractionMode(value, allowedModes = ["filters", "qa"]) {
  const mode = String(value || "").trim().toLowerCase();
  if ((mode === "filters" || mode === "qa") && allowedModes.includes(mode)) return mode;
  return allowedModes[0] || "filters";
}

export function toTitleCase(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function normalizeCartQty(value, fallback = 1) {
  const parsed = Number.parseInt(String(value ?? fallback), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

export function normalizeCartUom(value, fallback = "EA") {
  const raw = String(value || "").trim().toUpperCase();
  return raw || String(fallback || "EA").trim().toUpperCase() || "EA";
}

export function resolveProductUom(detail) {
  if (!detail || typeof detail !== "object") return "EA";
  const attributes = detail.attributes && typeof detail.attributes === "object" ? detail.attributes : {};
  return normalizeCartUom(
    detail.uom
    || detail.unit_of_measure
    || detail.unitOfMeasure
    || detail.unit
    || attributes.uom
    || attributes.unit_of_measure
    || attributes.unit
  );
}

export function normalizeProductCodeFilters(value) {
  if (Array.isArray(value)) {
    return [...new Set(value.map((entry) => String(entry || "").trim()).filter(Boolean))];
  }
  const raw = String(value || "").trim();
  if (!raw) return [];
  return [...new Set(raw.split(/[,\s]+/).map((entry) => String(entry || "").trim()).filter(Boolean))];
}

export function areStringListsEqual(left, right) {
  if (!Array.isArray(left) || !Array.isArray(right)) return false;
  if (left.length !== right.length) return false;
  for (let index = 0; index < left.length; index += 1) {
    if (String(left[index]) !== String(right[index])) return false;
  }
  return true;
}

export function humanizeFieldName(field) {
  const raw = String(field || "")
    .replace(/^rows_/, "")
    .replace(/^prodmap_/, "")
    .replace(/^prodattr_s_/, "")
    .replace(/_json$/i, "")
    .replace(/_/g, " ")
    .trim();
  if (!raw) return "-";
  return raw.replace(/\b\w/g, (char) => char.toUpperCase());
}
